import useLocalStorageState from 'use-local-storage-state';
import type { z } from 'zod';

import { useMe } from '@/features/authentication/_hooks/me';
import type { FilterConditionsModel } from '@/features/search/_models/local-storage';
import { generateHistoryDateTime } from '@/features/search/_utils/local-storage';
import { URL_PATH } from '@/utils/route-path';
import { generateSearchQuery } from '@/utils/search-params';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import {
  type WorkflowFilterHistoryStorageModel,
  WorkflowSearchAccordionOpenSettingModel,
  type WorkflowSearchAccordionOpenSettingValueModel,
} from '../models/local-storage';
import { WORKFLOW_SEARCH_ACCORDION_OPEN_SETTING_KEY } from '../utils/storage-keys';

export const useWorkflowSearchAccordionOpenSetting = (userId: string) => {
  const [
    workflowSearchAccordionOpenSetting,
    setWorkflowSearchAccordionOpenSetting,
  ] = useLocalStorageState<
    z.infer<typeof WorkflowSearchAccordionOpenSettingModel>
  >(WORKFLOW_SEARCH_ACCORDION_OPEN_SETTING_KEY);

  const workflowSearchAccordionOpenSettingParseResult =
    WorkflowSearchAccordionOpenSettingModel.safeParse(
      workflowSearchAccordionOpenSetting,
    );
  const parsedWorkflowSearchAccordionOpenSetting =
    workflowSearchAccordionOpenSettingParseResult.success
      ? workflowSearchAccordionOpenSettingParseResult.data
      : null;

  const searchAccordionOpenSettingByUser =
    parsedWorkflowSearchAccordionOpenSetting?.[userId];

  return {
    workflowSearchAccordionOpenSetting: searchAccordionOpenSettingByUser ?? {
      workflow: true,
    },
    setWorkflowSearchAccordionOpenSetting: (
      values: z.infer<typeof WorkflowSearchAccordionOpenSettingValueModel>,
    ) => {
      setWorkflowSearchAccordionOpenSetting((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    },
  };
};

export const useLastWorkflowSearchHistory = () => {
  const { data } = useMe();

  const userId = data?.id ?? '';

  type State = z.infer<typeof WorkflowFilterHistoryStorageModel>;

  const [searchHistoryStorage, setSearchHistoryStorage] =
    useLocalStorageState<State>('workflow-list-search-history');

  const lastSearched = searchHistoryStorage?.[userId]?.at(-1)?.filterConditions;

  const workflowPath = useMemo(() => {
    return generateSearchQuery({
      lastSearched,
      defaultPath: URL_PATH.workflow,
    });
  }, [lastSearched]);

  return {
    lastSearched,
    workflowPathWithQuery: workflowPath,
    setSearchHistoryStorage: (
      filterConditions: z.infer<typeof FilterConditionsModel>,
    ) => {
      setSearchHistoryStorage((prevValue) => {
        let searchHistory = [...(prevValue?.[userId] ?? [])];

        if (!isEqual(filterConditions, lastSearched)) {
          searchHistory = [
            {
              filterConditions,
              datetime: generateHistoryDateTime(),
            },
          ];
        }

        return {
          ...prevValue,
          [userId]: searchHistory,
        } as State;
      });
    },
  };
};
